import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { addNewPost, updatePost, getPostById } from '../../data/postsData';
import './index.css';

const WritePost = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');
  
  // 表单状态
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category: '生活随笔',
    tags: '',
    featuredImage: '',
    excerpt: '',
  });
  const [isPublishing, setIsPublishing] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const [preview, setPreview] = useState(false);
  const [isDraft, setIsDraft] = useState(false);
  const [draftId, setDraftId] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const editorRef = useRef(null);
  const draftSaveTimerRef = useRef(null);
  
  // 计算标签
  const tags = useMemo(() => {
    return formData.tags.split(' ').filter(tag => tag.trim());
  }, [formData.tags]);

  // 分类列表
  const categories = [
    { value: '生活随笔', label: '生活随笔', icon: '📝', color: '#FFB8D9' },
    { value: '技术分享', label: '技术分享', icon: '💻', color: '#8FD3FF' },
    { value: '读书笔记', label: '读书笔记', icon: '📚', color: '#FFD166' },
    { value: '旅行日记', label: '旅行日记', icon: '✈️', color: '#A8E6CF' },
    { value: '个人成长', label: '个人成长', icon: '🌱', color: '#C5C1FF' },
    { value: '美食分享', label: '美食分享', icon: '🍽️', color: '#FFB347' },
  ];

  // 分类颜色映射
  const categoryColors = {
    '生活随笔': 'linear-gradient(45deg, #FFB8D9, #FF6B9D)',
    '技术分享': 'linear-gradient(45deg, #8FD3FF, #3AA8FF)',
    '读书笔记': 'linear-gradient(45deg, #FFD166, #FF9A3D)',
    '旅行日记': 'linear-gradient(45deg, #A8E6CF, #4DB5A0)',
    '个人成长': 'linear-gradient(45deg, #C5C1FF, #8A84E2)',
    '美食分享': 'linear-gradient(45deg, #FFB347, #FF6B6B)'
  };

  // 作者信息
  const currentAuthor = {
    id: 1,
    name: '咖啡喵',
    role: '生活博主',
    avatar: '🐱',
    bio: '热爱生活，喜欢记录生活中的美好瞬间。相信每一天都有值得感恩的小确幸。'
  };

  // 写作小贴士
  const writingTips = [
    { icon: '✨', tip: '标题要吸引人，能概括文章核心' },
    { icon: '📖', tip: '开头要吸引读者继续阅读' },
    { icon: '🎯', tip: '段落清晰，每段表达一个核心观点' },
    { icon: '💭', tip: '适当添加个人思考和感悟' },
    { icon: '🔍', tip: '发布前仔细检查错别字' },
    { icon: '🐕', tip: '添加合适的小狗主题标签' },
  ];

  // 抽离loadLocalDraft为useCallback
  const loadLocalDraft = useCallback(() => {
    const savedDraft = localStorage.getItem('blog_draft');
    if (savedDraft) {
      try {
        const draft = JSON.parse(savedDraft);
        setFormData(draft);
        // 同步更新原生文本框内容
        if (editorRef.current) {
          editorRef.current.value = draft.content || '';
        }
        setIsDraft(true);
        setDraftId(draft.draftId || null);
        setCharCount(draft.content?.length || 0);
      } catch (error) {
        console.error('加载草稿失败:', error);
        localStorage.removeItem('blog_draft');
      }
    }
  }, []);

  // 初始化逻辑
  useEffect(() => {
    // 1. 编辑已有文章
    const initPostData = async () => {
      if (id) {
        const postToEdit = getPostById(parseInt(id), true);
        if (postToEdit) {
          const postData = {
            title: postToEdit.title || '',
            content: postToEdit.content || '',
            category: postToEdit.category || '生活随笔',
            tags: postToEdit.tags ? postToEdit.tags.join(' ') : '',
            featuredImage: postToEdit.featuredImage || '',
            excerpt: postToEdit.excerpt || '',
          };
          setFormData(postData);
          // 同步原生文本框
          if (editorRef.current) {
            editorRef.current.value = postData.content;
          }
          setIsEditing(true);
          setIsDraft(postToEdit.status === 'draft' || !postToEdit.published);
          setDraftId(postToEdit.id);
          setCharCount(postData.content?.length || 0);
        } else {
          alert('未找到该文章，将创建新文章');
          loadLocalDraft();
        }
      } else {
        // 2. 新建文章，加载本地草稿
        loadLocalDraft();
      }
    };

    initPostData();

    // 3. 监听页面关闭
    const handleBeforeUnload = (e) => {
      if (formData.title || formData.content) {
        e.preventDefault();
        e.returnValue = '你有未保存的内容，确定离开吗？';
        return '你有未保存的内容，确定离开吗？';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    // 清理函数
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (draftSaveTimerRef.current) {
        clearTimeout(draftSaveTimerRef.current);
      }
    };
  }, [id, loadLocalDraft,formData.content,formData.title]);

  // 计算阅读时间
  const calculateReadTime = useCallback((content) => {
    if (!content) return 1;
    const chineseCharCount = content.replace(/[^\u4e00-\u9fa5]/g, '').length;
    const englishCharCount = content.replace(/[\u4e00-\u9fa5]/g, '').length;
    const totalEffectiveChars = chineseCharCount + Math.floor(englishCharCount / 2);
    const minutes = Math.ceil(totalEffectiveChars / 300);
    return Math.max(minutes, 1);
  }, []);

  // 修复：原生输入处理（不再依赖React状态驱动输入）
  const handleNativeChange = (e) => {
    const { name, value } = e.target;
    // 直接更新状态，不干预光标
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (name === 'content') {
      setCharCount(value.length);
    }
    
    // 自动保存草稿
    if (draftSaveTimerRef.current) {
      clearTimeout(draftSaveTimerRef.current);
    }
    draftSaveTimerRef.current = setTimeout(autoSaveDraft, 1000);
  };

  // 分类选择
  const handleCategorySelect = (category) => {
    setFormData(prev => ({ ...prev, category }));
    autoSaveDraft();
  };

  // 图片上传
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const validTypes = ['image/jpeg', 'image/png', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      alert('仅支持JPG、PNG、GIF格式的图片');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      alert('图片大小不能超过5MB');
      return;
    }
    
    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData(prev => ({ ...prev, featuredImage: reader.result }));
      autoSaveDraft();
    };
    reader.readAsDataURL(file);
  };

  // 自动保存草稿
  const autoSaveDraft = useCallback(() => {
    if (!formData.title && !formData.content) return;
    
    const draftToSave = {
      ...formData,
      draftId: draftId || Date.now(),
      lastSaved: new Date().toLocaleString('zh-CN'),
      status: 'draft'
    };
    
    localStorage.setItem('blog_draft', JSON.stringify(draftToSave));
    setIsDraft(true);
    if (!draftId) {
      setDraftId(draftToSave.draftId);
    }
  }, [formData, draftId]);

  // 手动保存草稿
  const handleSaveDraft = () => {
    autoSaveDraft();
    
    if (isEditing && draftId) {
      const draftPost = {
        id: parseInt(draftId),
        title: formData.title.trim() || '未命名草稿',
        excerpt: formData.excerpt.trim() || formData.content.substring(0, 150) + '...',
        content: formData.content.trim(),
        date: new Date().toLocaleDateString('zh-CN'),
        category: formData.category,
        categoryColor: categoryColors[formData.category] || categoryColors['生活随笔'],
        tags: formData.tags.split(' ').filter(tag => tag.trim()),
        readTime: calculateReadTime(formData.content),
        author: currentAuthor,
        featured: false,
        published: false,
        status: 'draft',
        featuredImage: formData.featuredImage
      };
      updatePost(draftPost.id, draftPost);
    }
    
    alert('草稿已保存！💾 可以随时回来继续编辑');
  };

  // 清空草稿
  const handleClearDraft = () => {
    if (window.confirm('确定要清空当前草稿吗？此操作不可恢复！')) {
      localStorage.removeItem('blog_draft');
      setFormData({
        title: '',
        content: '',
        category: '生活随笔',
        tags: '',
        featuredImage: '',
        excerpt: '',
      });
      // 同步清空原生文本框
      if (editorRef.current) {
        editorRef.current.value = '';
      }
      setIsDraft(false);
      setDraftId(null);
      setCharCount(0);
      alert('草稿已清空！🗑️ 可以开始新的创作了');
    }
  };

  // 发布文章
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      alert('请输入文章标题');
      return;
    }
    
    // 从原生文本框获取最终内容（确保最新）
    const finalContent = editorRef.current ? editorRef.current.value : formData.content;
    if (!finalContent.trim()) {
      alert('请输入文章内容');
      return;
    }
    
    const tags = formData.tags.split(' ').filter(tag => tag.trim());
    if (tags.length > 5) {
      alert('最多只能添加5个标签，请删除多余的标签');
      return;
    }
    
    setIsPublishing(true);
    
    try {
      const postData = {
        title: formData.title.trim(),
        excerpt: formData.excerpt.trim() || finalContent.substring(0, 150) + '...',
        content: finalContent.trim(),
        date: new Date().toLocaleDateString('zh-CN'),
        category: formData.category,
        categoryColor: categoryColors[formData.category] || categoryColors['生活随笔'],
        tags: tags,
        readTime: calculateReadTime(finalContent),
        author: currentAuthor,
        featured: false,
        published: true,
        status: 'published',
        featuredImage: formData.featuredImage,
        likes: 0,
        views: 0,
        comments: 0
      };
      
      let newPost;
      if (isEditing) {
        newPost = updatePost(parseInt(id), postData);
      } else {
        newPost = addNewPost(postData);
      }
      
      console.log('文章发布成功:', newPost);
      
      localStorage.removeItem('blog_draft');
      setIsDraft(false);
      
      alert(`🎉 文章《${newPost.title}》${isEditing ? '更新' : '发布'}成功！`);
      navigate(`/post/${newPost.id}`);
      
    } catch (error) {
      console.error('文章发布失败:', error);
      alert('❌ 文章发布失败，请重试！');
    } finally {
      setIsPublishing(false);
    }
  };

  // 预览切换
  const handlePreview = () => {
    // 同步原生文本框内容到状态，用于预览
    if (editorRef.current) {
      setFormData(prev => ({
        ...prev,
        content: editorRef.current.value
      }));
    }
    setPreview(!preview);
  };

  // 终极修复：原生DOM操作格式化（完全绕过React状态）
  const handleFormatText = (format) => {
    if (!editorRef.current) return;
    const textarea = editorRef.current;
    
    // 强制聚焦
    textarea.focus();
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = textarea.value.substring(start, end);
    
    // 定义格式化规则
    const formatRules = {
      heading1: { prefix: '# ', suffix: '', placeholder: '一级标题' },
      heading2: { prefix: '## ', suffix: '', placeholder: '二级标题' },
      bold: { prefix: '**', suffix: '**', placeholder: '加粗文本' },
      italic: { prefix: '*', suffix: '*', placeholder: '斜体文本' },
      quote: { prefix: '> ', suffix: '', placeholder: '引用文本' },
      link: { prefix: '[', suffix: '](https://example.com)', placeholder: '链接文本' }
    };
    
    const rule = formatRules[format];
    if (!rule) return;
    
    // 原生DOM直接修改内容（不通过React状态）
    let newContent, cursorStart, cursorEnd;
    if (selectedText) {
      // 有选中文字：包裹格式化符号
      newContent = textarea.value.substring(0, start) + 
                  rule.prefix + selectedText + rule.suffix + 
                  textarea.value.substring(end);
      // 光标定位到格式化文本末尾
      cursorStart = cursorEnd = start + rule.prefix.length + selectedText.length + rule.suffix.length;
    } else {
      // 无选中文字：插入格式化内容+占位符
      newContent = textarea.value.substring(0, start) + 
                  rule.prefix + rule.placeholder + rule.suffix + 
                  textarea.value.substring(end);
      // 光标定位到占位符中间（可直接输入替换）
      cursorStart = start + rule.prefix.length;
      cursorEnd = start + rule.prefix.length + rule.placeholder.length;
    }
    
    // 1. 原生修改文本框内容
    textarea.value = newContent;
    // 2. 原生定位光标（关键：不依赖React）
    textarea.setSelectionRange(cursorStart, cursorEnd);
    // 3. 同步更新React状态（仅用于保存/发布，不影响输入）
    setFormData(prev => ({ ...prev, content: newContent }));
    setCharCount(newContent.length);
    
    // 4. 自动保存
    autoSaveDraft();
  };

  // 计算词数（从原生文本框获取最新内容）
  const wordCount = useMemo(() => {
    const content = editorRef.current ? editorRef.current.value : formData.content;
    return content.trim().split(/\s+/).filter(word => word).length;
  }, [formData.content]);

  // 页面标题
  const pageTitle = isEditing ? (isDraft ? '编辑草稿' : '编辑文章') : '写文章';

  return (
    <div className="write-post-page fade-in">
      <div className="write-header">
        <div className="header-content">
          <h1>✏️ {pageTitle}</h1>
          <p className="header-subtitle">
            {isEditing 
              ? (isDraft ? '继续完善你的草稿，随时可以发布' : '修改你的文章内容，更新发布')
              : '记录生活，分享思考，创造美好回忆'}
          </p>
          {isDraft && (
            <div className="draft-indicator">
              <span className="draft-icon">💾</span>
              <span className="draft-text">草稿已自动保存</span>
              <button 
                type="button" 
                className="clear-draft-btn"
                onClick={handleClearDraft}
              >
                清空草稿
              </button>
            </div>
          )}
        </div>
        <div className="header-decoration">
          <span className="decoration-icon">🐕</span>
          <span className="decoration-icon">📝</span>
          <span className="decoration-icon">✨</span>
        </div>
      </div>

      <div className="write-container">
        <div className="editor-section">
          <form onSubmit={handleSubmit} className="write-form">
            <div className="form-group title-group">
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleNativeChange}
                placeholder={isEditing ? '修改文章标题...' : '给文章起个吸引人的标题...'}
                className="form-control title-input"
                required
                maxLength="100"
              />
              <div className="title-count">
                {formData.title.length}/100
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">选择分类</label>
              <div className="category-selector">
                {categories.map(cat => (
                  <button
                    key={cat.value}
                    type="button"
                    className={`category-option ${formData.category === cat.value ? 'active' : ''}`}
                    style={{ '--category-color': cat.color }}
                    onClick={() => handleCategorySelect(cat.value)}
                  >
                    <span className="category-icon">{cat.icon}</span>
                    <span className="category-label">{cat.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="form-group">
              <div className="form-label-row">
                <label className="form-label">文章标签</label>
                <span className="tag-hint">（用空格分隔，最多5个，当前：{tags.length}/5）</span>
              </div>
              <div className="tag-input-container">
                <input
                  type="text"
                  name="tags"
                  value={formData.tags}
                  onChange={handleNativeChange}
                  placeholder="如：生活 思考 成长"
                  className="form-control tag-input"
                  maxLength="100"
                />
              </div>
              <div className="tag-examples">
                <span className="tag-example">快速添加：</span>
                {['生活', '思考', '成长', '技术', '读书', '旅行', '美食', '摄影'].map(tag => (
                  <button
                    key={tag}
                    type="button"
                    className="tag-example-btn"
                    onClick={() => {
                      const currentTags = formData.tags.split(' ').filter(t => t.trim());
                      if (!currentTags.includes(tag) && currentTags.length < 5) {
                        setFormData(prev => ({
                          ...prev,
                          tags: [...currentTags, tag].join(' ')
                        }));
                      } else if (currentTags.length >= 5) {
                        alert('最多只能添加5个标签');
                      }
                    }}
                  >
                    #{tag}
                  </button>
                ))}
              </div>
              {tags.length >= 5 && (
                <div className="tag-limit-warning">
                  ⚠️ 最多只能添加5个标签，请删除多余的标签
                </div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">文章摘要</label>
              <textarea
                name="excerpt"
                value={formData.excerpt}
                onChange={handleNativeChange}
                placeholder="写一段简短的摘要，吸引读者阅读全文..."
                className="form-control excerpt-input"
                rows="3"
                maxLength="200"
              />
              <div className="excerpt-count">
                {formData.excerpt.length}/200
              </div>
            </div>

            <div className="form-group">
              <div className="editor-header">
                <label className="form-label">文章内容</label>
                <div className="editor-tools">
                  <button
                    type="button"
                    className="editor-tool-btn"
                    onClick={() => handleFormatText('heading1')}
                    title="一级标题"
                  >
                    H1
                  </button>
                  <button
                    type="button"
                    className="editor-tool-btn"
                    onClick={() => handleFormatText('heading2')}
                    title="二级标题"
                  >
                    H2
                  </button>
                  <button
                    type="button"
                    className="editor-tool-btn"
                    onClick={() => handleFormatText('bold')}
                    title="加粗"
                  >
                    <strong>B</strong>
                  </button>
                  <button
                    type="button"
                    className="editor-tool-btn"
                    onClick={() => handleFormatText('italic')}
                    title="斜体"
                  >
                    <em>I</em>
                  </button>
                  <button
                    type="button"
                    className="editor-tool-btn"
                    onClick={() => handleFormatText('quote')}
                    title="引用"
                  >
                    ❝
                  </button>
                  <button
                    type="button"
                    className="editor-tool-btn"
                    onClick={() => handleFormatText('link')}
                    title="链接"
                  >
                    🔗
                  </button>
                </div>
              </div>
              {/* 核心：移除value绑定，改用原生DOM操作 */}
              <textarea
                ref={editorRef}
                name="content"
                onChange={handleNativeChange}
                placeholder={isEditing ? '修改你的文章内容...' : '开始你的创作吧！支持Markdown格式...'}
                className="form-control content-editor"
                rows="20"
                required
                style={{
                  pointerEvents: 'auto !important',
                  userSelect: 'auto !important',
                  position: 'relative',
                  zIndex: 9999,
                  border: '1px solid #ccc',
                  padding: '10px',
                  fontSize: '16px'
                }}
              />
              <div className="editor-footer">
                <div className="char-count">
                  <span className="char-count-label">字数统计：</span>
                  <span className="char-count-number">{wordCount} 词</span>
                  <span className="char-count-separator">·</span>
                  <span className="char-count-number">{charCount} 字符</span>
                  <span className="char-count-separator">·</span>
                  <span className="char-count-number">约{calculateReadTime(formData.content)}分钟阅读</span>
                </div>
                <div className="editor-hint">
                  <span className="hint-icon">💡</span>
                  支持Markdown语法，{isDraft ? '已自动保存草稿' : '离开前记得保存'}
                </div>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">特色图片（可选）</label>
              <div className="image-upload-container">
                {formData.featuredImage ? (
                  <div className="image-preview">
                    <img src={formData.featuredImage} alt="预览" />
                    <button
                      type="button"
                      className="remove-image-btn"
                      onClick={() => {
                        setFormData(prev => ({ ...prev, featuredImage: '' }));
                        autoSaveDraft();
                      }}
                    >
                      ✕ 移除
                    </button>
                  </div>
                ) : (
                  <label className="image-upload-area">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="image-upload-input"
                    />
                    <span className="upload-icon">🖼️</span>
                    <span className="upload-text">点击上传特色图片</span>
                    <span className="upload-hint">支持JPG、PNG、GIF格式，最大5MB</span>
                  </label>
                )}
              </div>
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="btn btn-secondary draft-btn"
                onClick={handleSaveDraft}
                disabled={isPublishing}
              >
                <span className="btn-icon">💾</span>
                {isEditing ? (isDraft ? '保存草稿' : '保存为草稿') : '保存草稿'}
              </button>
              
              <div className="preview-group">
                <button
                  type="button"
                  className="preview-btn"
                  onClick={handlePreview}
                  disabled={isPublishing}
                >
                  {preview ? '关闭预览' : '预览文章'}
                </button>
              </div>
              
              <button
                type="submit"
                className="btn btn-primary publish-btn"
                disabled={isPublishing}
              >
                {isPublishing ? (
                  <>
                    <span className="loading-spinner"></span>
                    {isEditing ? '更新中...' : '发布中...'}
                  </>
                ) : (
                  <>
                    <span className="btn-icon">🚀</span>
                    {isEditing ? '更新文章' : '发布文章'}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        <div className="sidebar-section">
          <div className="writing-tips-card">
            <h3>💡 写作小贴士</h3>
            <div className="tips-list">
              {writingTips.map((item, index) => (
                <div key={index} className="tip-item">
                  <span className="tip-icon">{item.icon}</span>
                  <span className="tip-text">{item.tip}</span>
                </div>
              ))}
            </div>
          </div>

          {(isDraft || isEditing) && (
            <div className="draft-status-card">
              <h3>💾 {isEditing ? (isDraft ? '草稿状态' : '编辑状态') : '草稿状态'}</h3>
              <div className="draft-info">
                {isDraft && (
                  <div className="draft-item">
                    <span className="draft-label">最后保存：</span>
                    <span className="draft-value">
                      {JSON.parse(localStorage.getItem('blog_draft') || '{}').lastSaved || '刚刚'}
                    </span>
                  </div>
                )}
                <div className="draft-item">
                  <span className="draft-label">文章字数：</span>
                  <span className="draft-value">{wordCount} 词 ({charCount} 字符)</span>
                </div>
                <div className="draft-item">
                  <span className="draft-label">预计阅读：</span>
                  <span className="draft-value">{calculateReadTime(formData.content)} 分钟</span>
                </div>
                {(isDraft || isEditing) && (
                  <div className="draft-item">
                    <span className="draft-label">文章ID：</span>
                    <span className="draft-value">{draftId || id || '未分配'}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="markdown-card">
            <h3>📝 Markdown 快速参考</h3>
            <div className="markdown-examples">
              <div className="markdown-example">
                <code># 一级标题</code>
                <span>大标题</span>
              </div>
              <div className="markdown-example">
                <code>## 二级标题</code>
                <span>小标题</span>
              </div>
              <div className="markdown-example">
                <code>**粗体**</code>
                <span>粗体文本</span>
              </div>
              <div className="markdown-example">
                <code>*斜体*</code>
                <span>斜体文本</span>
              </div>
              <div className="markdown-example">
                <code>[链接](URL)</code>
                <span>添加链接</span>
              </div>
              <div className="markdown-example">
                <code>&gt; 引用</code>
                <span>引用文本</span>
              </div>
              <div className="markdown-example">
                <code>- 列表项</code>
                <span>无序列表</span>
              </div>
              <div className="markdown-example">
                <code>1. 列表项</code>
                <span>有序列表</span>
              </div>
            </div>
          </div>

          {preview && (
            <div className="preview-card">
              <h3>👁️ 文章预览</h3>
              <div className="preview-content">
                <h4 className="preview-title">{formData.title || '无标题'}</h4>
                <div className="preview-meta">
                  <span 
                    className="preview-category"
                    style={{ 
                      background: categoryColors[formData.category] || categoryColors['生活随笔']
                    }}
                  >
                    {formData.category}
                  </span>
                  <span className="preview-date">{new Date().toLocaleDateString('zh-CN')}</span>
                  <span className="preview-readtime">{calculateReadTime(formData.content)}分钟阅读</span>
                </div>
                <div className="preview-excerpt">
                  {formData.excerpt || (formData.content ? formData.content.substring(0, 150) + '...' : '暂无摘要')}
                </div>
                {tags.length > 0 && (
                  <div className="preview-tags">
                    {tags.map((tag, index) => (
                      <span key={index} className="preview-tag">
                        #{tag}
                      </span>
                    ))}
                  </div>
                )}
                {formData.featuredImage && (
                  <div className="preview-image">
                    <img 
                      src={formData.featuredImage} 
                      alt="特色图片预览" 
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="encouragement-card">
            <div className="encouragement-content">
              <span className="dog-icon">🐕</span>
              <h4>{isEditing ? '加油修改！' : '加油写作！'}</h4>
              <p>{isEditing 
                ? '你的每一次修改都会让文章更完美，小狗会一直陪着你！' 
                : '你的每一个字都很珍贵，小狗会一直陪着你！'}</p>
            </div>
            <div className="paw-trail">
              <span>🐾</span>
              <span>🐾</span>
              <span>🐾</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WritePost;